<template>
  <div class="integration-container">
    <base-nav
      title="Интеграции"
      :show-back="true"
      back-fallback="Home"
    >
      <base-primary-button
        icon="addSquare"
        v-if="activeTab === 'glonasssoft'"
        class="nav-btn"
        @click="isAddIntegrationModalVisible = true"
      >
        Создать интеграцию ГЛОНАССсофт
      </base-primary-button>
      <base-primary-button
        icon="addSquare"
        v-if="activeTab === 'saby'"
        class="nav-btn"
        @click="isAddSabyModalVisible = true"
      >
        Создать интеграцию Saby
      </base-primary-button>
      <base-primary-button
        icon="addSquare"
        v-if="activeTab === 'chats'"
        class="nav-btn"
        @click="isAddMaxBotModalVisible = true"
      >
        Создать бота
      </base-primary-button>
    </base-nav>

    <BaseTabs
      v-model="activeTab"
      :tabs="tabs"
      class="integrations-tabs"
      @middle-click="
        openRouteInNewTab({
          name: 'Integrations',
          query: { ...$route.query, tab: $event.key },
        })
      "
    />

    <!-- Вкладка ГЛОНАССсофт -->
    <div v-if="activeTab === 'glonasssoft'" class="tab-content">
      <base-loader v-if="dataIsLoading" />
      <div
        class="integrations-block"
        v-else-if="glonasssoftIntegrations.length > 0"
      >
        <ul class="integration-list">
          <li
            class="card integration-item"
            v-for="(integration, index) in glonasssoftIntegrations"
            :key="index"
          >
            <div class="item-header">
              <h3 class="item-title">Интеграция Glonasssoft</h3>
              <div class="item-status">
                <span class="status-title">
                  {{ integration.enabled ? "Подключено" : "Отключено" }}
                </span>
                <base-toggle-switch
                  :model-value="integration.enabled"
                  @update:model-value="toggleIntegration(integration)"
                  :disabled="loading"
                />
              </div>
            </div>

            <span class="item-value">
              Логин: {{ integration.login }}
            </span>

            <div class="item-actions">
              <base-button
                @click="checkConnection(integration.id, index)"
                class="action-btn"
                :disabled="loading || cardStates[index]?.checkingConnection"
              >
                {{ cardStates[index]?.checkingConnection ? "Проверка..." : "Проверить подключение" }}
              </base-button>

              <base-button
                @click="importVehicles(integration.id, index)"
                class="action-btn btn-import"
                :disabled="loading || cardStates[index]?.importingVehicles"
              >
                {{ cardStates[index]?.importingVehicles ? "Загрузка техники..." : "Импортировать технику" }}
              </base-button>
            </div>
            <div class="item-bottom" v-if="integration.created_at">
              <MetaTime :created-at="integration.created_at" />
            </div>
          </li>
        </ul>
      </div>
      <div class="integrations-info" v-else>
        <span class="no-data">Нет интеграций</span>
      </div>
    </div>

    <!-- Вкладка Трекеры -->
    <div v-if="activeTab === 'trackers'" class="tab-content">
      <TelemetryTrackersSection />
    </div>

    <!-- Вкладка Saby (ЭДО) -->
    <div v-if="activeTab === 'saby'" class="tab-content">
      <base-loader v-if="dataIsLoading" />
      <ul v-else-if="sabyIntegrations.length > 0" class="integration-list">
        <SabyIntegrationCard
          v-for="integration in sabyIntegrations"
          :key="integration.id"
          :integration="integration"
          :is-loading="loading"
          @toggle="toggleIntegration"
          @save="saveSabyEdit"
        />
      </ul>
      <div class="integrations-info" v-else>
        <span class="no-data">Нет интеграций Saby</span>
      </div>
    </div>

    <!-- Вкладка чат-ботов поддержки -->
    <div v-if="activeTab === 'chats'" class="tab-content">
      <MaxBotConnectionGuide class="chat-guide" />
      <base-loader v-if="chatProvidersAreLoading" />
      <ul v-else-if="maxBotProviders.length > 0" class="integration-list">
        <ChatProviderCard
          v-for="provider in maxBotProviders"
          :key="provider.id"
          :provider="provider"
          :is-loading="chatProviderIsSubmitting"
          :clickable="true"
          @edit="startChatProviderEdit"
          @primary-action="startChatProviderEdit"
          @check-webhook="checkChatProviderWebhook"
          @toggle="toggleChatProvider"
        />
      </ul>
      <BaseEmptyState v-else title="Нет подключенных ботов" icon="comment" />
    </div>
  </div>

  <add-saby-integration-modal
    v-if="isAddSabyModalVisible"
    @close="isAddSabyModalVisible = false"
    @action-created="actionCreateSuccess"
  />

  <add-glonasssoft-integration-modal
    v-if="isAddIntegrationModalVisible"
    @close="isAddIntegrationModalVisible = false"
    @action-created="actionCreateSuccess"
  />

  <MaxBotIntegrationModal
    v-if="maxBotModalIsVisible"
    :initial-data="editingChatProvider"
    @close="closeMaxBotModal"
    @action-saved="chatProviderSaveSuccess"
  />
</template>

<script>
import { openRouteInNewTab } from "@/utils/routeUtils";

import AddGlonasssoftIntegrationModal from "@/components/AddGlonasssoftIntegrationModal.vue";
import MaxBotIntegrationModal from "@/components/Integration/MaxBotIntegrationModal.vue";
import AddSabyIntegrationModal from "@/components/AddSabyIntegrationModal.vue";
import ChatProviderCard from "@/components/Integration/ChatProviderCard.vue";
import MaxBotConnectionGuide from "@/components/Integration/MaxBotConnectionGuide.vue";
import SabyIntegrationCard from "@/components/Integration/SabyIntegrationCard.vue";
import MetaTime from "@/components/Meta/MetaTime.vue";
import TelemetryTrackersSection from "@/components/Telemetry/TelemetryTrackersSection.vue";
import { useChatProviderStore } from "@/stores/chatProviderStore";
import { useIntegrationStore } from "@/stores/integrationStore";

const VALID_TABS = ["glonasssoft", "trackers", "saby", "chats"];

export default {
  name: "IntegrationsPage",
  components: {
    AddGlonasssoftIntegrationModal,
    MaxBotIntegrationModal,
    AddSabyIntegrationModal,
    ChatProviderCard,
    MaxBotConnectionGuide,
    SabyIntegrationCard,
    MetaTime,
    TelemetryTrackersSection,
  },
  data() {
    return {
      activeTab: "trackers",
      tabs: [
        { key: "trackers", label: "Трекеры", icon: "🛰️" },
        { key: "glonasssoft", label: "ГЛОНАССсофт", icon: "📡" },
        { key: "saby", label: "Saby (ЭДО)", icon: "📄" },
        { key: "chats", label: "Чаты", icon: "💬" },
      ],
      isAddIntegrationModalVisible: false,
      isAddSabyModalVisible: false,
      isAddMaxBotModalVisible: false,
      editingChatProvider: null,
      cardStates: {},
    };
  },
  watch: {
    "$route.query": {
      handler(query) {
        this.handleRouteQuery(query);
      },
      immediate: true,
    },
    activeTab(newTab) {
      this.updateUrlTab(newTab);
    },
  },
  computed: {
    integrationStore() {
      return useIntegrationStore();
    },
    chatProviderStore() {
      return useChatProviderStore();
    },
    dataIsLoading() {
      return this.integrationStore.dataIsLoading;
    },
    loading() {
      return this.integrationStore.isSubmitting;
    },
    glonasssoftIntegrations() {
      return this.integrationStore.glonasssoftIntegrations;
    },
    sabyIntegrations() {
      return this.integrationStore.sabyIntegrations;
    },
    chatProvidersAreLoading() {
      return this.chatProviderStore.dataIsLoading;
    },
    chatProviderIsSubmitting() {
      return this.chatProviderStore.isSubmitting;
    },
    maxBotProviders() {
      return this.chatProviderStore.maxBotProviders;
    },
    maxBotModalIsVisible() {
      return this.isAddMaxBotModalVisible || Boolean(this.editingChatProvider);
    },
  },
  methods: {
    async getIntegrationsRequest() {
      await this.integrationStore.fetchIntegrations();
      this.resetCardStates();
    },

    async getChatProvidersRequest() {
      await this.chatProviderStore.fetchChatProviders();
    },

    resetCardStates() {
      const states = {};
      this.glonasssoftIntegrations.forEach((_, index) => {
        states[index] = {
          checkingConnection: false,
          importingVehicles: false,
        };
      });
      this.cardStates = states;
    },

    async toggleIntegration(integration) {
      await this.integrationStore.toggleIntegration(integration);
      this.resetCardStates();
    },

    async checkConnection(integrationId, cardIndex) {
      this.cardStates = {
        ...this.cardStates,
        [cardIndex]: { ...this.cardStates[cardIndex], checkingConnection: true },
      };
      try {
        await this.integrationStore.checkConnection(integrationId);
      } finally {
        this.cardStates = {
          ...this.cardStates,
          [cardIndex]: { ...this.cardStates[cardIndex], checkingConnection: false },
        };
      }
    },

    async importVehicles(integrationId, cardIndex) {
      this.cardStates = {
        ...this.cardStates,
        [cardIndex]: { ...this.cardStates[cardIndex], importingVehicles: true },
      };
      try {
        await this.integrationStore.importVehicles(integrationId);
      } finally {
        this.cardStates = {
          ...this.cardStates,
          [cardIndex]: { ...this.cardStates[cardIndex], importingVehicles: false },
        };
      }
    },

    async actionCreateSuccess() {
      await this.getIntegrationsRequest();
    },

    async saveSabyEdit({ id, payload }) {
      await this.integrationStore.updateIntegration(id, payload);
      this.resetCardStates();
    },

    async chatProviderSaveSuccess() {
      await this.getChatProvidersRequest();
    },

    startChatProviderEdit(provider) {
      this.isAddMaxBotModalVisible = false;
      this.editingChatProvider = provider;
    },

    closeMaxBotModal() {
      this.isAddMaxBotModalVisible = false;
      this.editingChatProvider = null;
    },

    async toggleChatProvider(provider) {
      await this.chatProviderStore.toggleChatProvider(provider);
    },

    async checkChatProviderWebhook(provider) {
      await this.chatProviderStore.checkChatProviderWebhook(provider.id);
    },

    handleRouteQuery(query) {
      if (query.tab && VALID_TABS.includes(query.tab)) {
        this.activeTab = query.tab;
      } else {
        this.updateUrlTab(this.activeTab);
      }
    },

    updateUrlTab(tabName) {
      if (this.$route.query.tab === tabName) return;

      this.$router.replace({ query: { ...this.$route.query, tab: tabName } });
    },

    openRouteInNewTab,
  },
  async created() {
    await this.getIntegrationsRequest();
    await this.getChatProvidersRequest();
    this.updateUrlTab(this.activeTab);
  },
};
</script>

<style scoped>
.integrations-tabs {
  margin-bottom: var(--base-out-indent);
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.integration-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.chat-guide {
  margin-bottom: var(--base-out-indent);
}

.integration-item:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin-bottom: var(--base-in-indent);
}

.item-title { color: #666; }

.item-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.action-btn { padding: 7px 10px; }
.action-btn:not(:last-child) { margin-bottom: 10px; }

.btn-import:hover {
  background-color: #4f46e5 !important;
  color: #fff;
}

.item-status { display: flex; align-items: center; }
.status-title { margin-right: 10px; }
.item-value { margin-bottom: var(--base-in-indent); }
.item-bottom { margin-top: var(--base-in-indent); }

@media (max-width: 768px) {
  .integration-header {
    gap: 15px;
    margin-bottom: var(--base-in-indent);
  }
  .item-header {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
}
</style>
